import {ChangeDetectionStrategy, Component, EventEmitter, OnInit, Optional} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {NbWindowRef} from '@nebular/theme';
import {
  BaseComponent,
  FeatureTypes,
  IdAndHeader,
  TemplateFeature,
  TemplateFeatureDestMapping,
  TemplateFeatureGroup,
  TemplatesMappingInfo,
} from 'compob2b-libs';
import {map, takeUntil} from 'rxjs/operators';
import {translations} from '../../../../../../environments/translations.ru';
import {TemplateFeatureValueMapperComponent} from '../template-feature-value-mapper/template-feature-value-mapper.component';

type FeaturesMapping = {
  [key: number]: TemplateFeatureDestMapping
};

@Component({
  selector: 'app-feature-mapper',
  templateUrl: './feature-mapper.component.html',
  styleUrls: ['./feature-mapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureMapperComponent extends BaseComponent implements OnInit {

  private sourceTemplateId: number;
  private destTemplateId: number;

  templateFrom: IdAndHeader;
  templateTo: IdAndHeader;
  templateGroupsFrom: TemplateFeatureGroup[];

  selectTemplateFeaturesTo: TemplateFeature[];
  multipleSelectTemplateFeaturesTo: TemplateFeature[];
  colorSelectTemplateFeaturesTo: TemplateFeature[];
  rangeTemplateFeaturesTo: TemplateFeature[];
  booleanTemplateFeaturesTo: TemplateFeature[];
  iconTemplateFeaturesTo: TemplateFeature[];
  intervalTemplateFeaturesTo: TemplateFeature[];
  stringTemplateFeaturesTo: TemplateFeature[];
  htmlTemplateFeaturesTo: TemplateFeature[];
  sizeTemplateFeaturesTo: TemplateFeature[];
  linkTemplateFeaturesTo: TemplateFeature[];
  smartlistTemplateFeaturesTo: TemplateFeature[];

  updateMapping = new EventEmitter<boolean>();
  public updateTemplateMapping: EventEmitter<boolean>;
  formControl: FormControl;

  form = new FormGroup({}, this.uniqueRequiredDestFeatureMappingValidator);

  constructor(@Optional() protected readonly windowRef: NbWindowRef) {
    super();
  }

  uniqueRequiredDestFeatureMappingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control as FormGroup;

      const destFeatureIDs = new Set<number>();
      for (const sourceFeatureID in formGroup.controls) {
        const value = formGroup.controls[sourceFeatureID].get('destFeatureId').value;
        const destFeatureID: number = value ? parseInt(value, 10) : null;
        if (destFeatureID === null) {
          continue;
        }

        if (destFeatureIDs.has(destFeatureID)) {
          return {featureUnique: true};
        }

        destFeatureIDs.add(destFeatureID);
      }

      if (!destFeatureIDs.size) {
        return {featureRequired: true};
      }

      return null;
    };
  }

  ngOnInit(): void {
    this.compoApiService.get<TemplatesMappingInfo>(`/template/${this.sourceTemplateId}/map-to/${this.destTemplateId}`)
      .pipe(
        map(response => response.data),
        takeUntil(this.unsubscribe),
      )
      .subscribe(response => {
        this.templateFrom = response.templateFrom;
        this.templateTo = response.templateTo;
        this.templateGroupsFrom = response.templateGroupsFrom;

        this.templateGroupsFrom.forEach(groupItem => {
          groupItem.features.forEach(featureItem => {
            this.form.addControl(featureItem.id.toString(), new FormGroup({
              destFeatureId: new FormControl(),
              valueMappings: new FormArray([]),
            }));
          });
        });

        if (this.formControl.value) {
          for (const sourceFeatureId in this.formControl.value) {
            const valueElement: TemplateFeatureDestMapping = this.formControl.value[sourceFeatureId];

            if (this.form.contains(sourceFeatureId) && valueElement) {
              const control = this.form.get(sourceFeatureId);
              control.patchValue({destFeatureId: this.formControl.value[sourceFeatureId].destFeatureId});

              if (valueElement.valueMappings) {
                const valueMappings = control.get('valueMappings') as FormArray;
                valueMappings.clear();
                valueElement.valueMappings.forEach(item => {
                  valueMappings.push(TemplateFeatureValueMapperComponent.createNewValueMapping(item.sourceValueId, item.destValueId));
                });
              }
            }
          }
        }

        this.createFeaturesLists(response.templateFeaturesTo);
        this.cdr.detectChanges();
      });

    this.updateMapping.pipe(takeUntil(this.unsubscribe)).subscribe(_ => this.cdr.detectChanges());
  }

  private createFeaturesLists(features: TemplateFeature[]): void {
    this.selectTemplateFeaturesTo = features.filter(({featureTypeId}) => [FeatureTypes.SELECT, FeatureTypes.CHECKBOX].includes(featureTypeId));

    this.multipleSelectTemplateFeaturesTo = features.filter(({featureTypeId, multiple}) => featureTypeId === FeatureTypes.CHECKBOX
      || (featureTypeId === FeatureTypes.SELECT && multiple === true));

    this.colorSelectTemplateFeaturesTo = features
      .filter(({featureTypeId, multiple}) => featureTypeId === FeatureTypes.COLOR
        || (featureTypeId === FeatureTypes.SELECT && multiple === false));

    this.rangeTemplateFeaturesTo = features
      .filter(({featureTypeId}) => [FeatureTypes.RANGE, FeatureTypes.SELECT].includes(featureTypeId));

    this.booleanTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.BOOLEAN);

    this.iconTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.ICON);

    this.intervalTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.INTERVAL);

    this.stringTemplateFeaturesTo = features
      .filter(({featureTypeId}) => [FeatureTypes.STRING, FeatureTypes.SELECT, FeatureTypes.HTML].includes(featureTypeId));

    this.htmlTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.HTML);

    this.sizeTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.SIZE);

    this.linkTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.LINK);

    this.smartlistTemplateFeaturesTo = features.filter(({featureTypeId}) => featureTypeId === FeatureTypes.SMARTLIST);
  }

  filterAvailableFeatures(feature: TemplateFeature): TemplateFeature[] {
    if (this.isSelectableFeatureType(feature.featureTypeId)) {
      if ((feature.featureTypeId === FeatureTypes.SELECT && feature.multiple === true)
        || feature.featureTypeId === FeatureTypes.CHECKBOX) {
        return this.multipleSelectTemplateFeaturesTo;
      }

      if (feature.featureTypeId === FeatureTypes.COLOR) {
        return this.colorSelectTemplateFeaturesTo;
      }

      return this.selectTemplateFeaturesTo;
    }

    if (this.isRangeFeatureType(feature.featureTypeId)) {
      return this.rangeTemplateFeaturesTo;
    }

    if (this.isBooleanFeatureType(feature.featureTypeId)) {
      return this.booleanTemplateFeaturesTo;
    }

    if (this.isIconFeatureType(feature.featureTypeId)) {
      return this.iconTemplateFeaturesTo;
    }

    if (this.isIntervalFeatureType(feature.featureTypeId)) {
      return this.intervalTemplateFeaturesTo;
    }

    if (this.isStringFeatureType(feature.featureTypeId)) {
      return this.stringTemplateFeaturesTo;
    }

    if (this.isHtmlFeatureType(feature.featureTypeId)) {
      return this.htmlTemplateFeaturesTo;
    }

    if (this.isSizeFeatureType(feature.featureTypeId)) {
      return this.sizeTemplateFeaturesTo;
    }

    if (this.isLinkFeatureType(feature.featureTypeId)) {
      return this.linkTemplateFeaturesTo;
    }

    if (this.isSmartlistFeatureType(feature.featureTypeId)) {
      return this.smartlistTemplateFeaturesTo;
    }

    return [];
  }

  isSelectableFeatureType(featureTypeID: number): boolean {
    return [FeatureTypes.SELECT, FeatureTypes.CHECKBOX, FeatureTypes.COLOR].includes(featureTypeID);
  }

  isRangeFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.RANGE;
  }

  isBooleanFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.BOOLEAN;
  }

  isIconFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.ICON;
  }

  isIntervalFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.INTERVAL;
  }

  isStringFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.STRING;
  }

  isHtmlFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.HTML;
  }

  isSizeFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.SIZE;
  }

  isLinkFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.LINK;
  }

  isSmartlistFeatureType(featureTypeID: number): boolean {
    return featureTypeID === FeatureTypes.SMARTLIST;
  }

  setFeatureValueMapping(feature: TemplateFeature) {
    if (!feature.id || !this.form.get(feature.id.toString()).value?.destFeatureId) {
      return;
    }

    this.windowService.open(TemplateFeatureValueMapperComponent, {
      title: translations.templateFeatureValueMapping,
      windowClass: 'col-sm-10',
      context: {
        sourceFeatureId: feature.id,
        destFeatureId: parseInt(this.form.get(feature.id.toString()).value.destFeatureId, 10),
        form: this.form.get(feature.id.toString()) as FormGroup,
        updateMapping: this.updateMapping
      },
    });
  }

  selectFeature(featureTo: TemplateFeature, featureFrom: TemplateFeature) {
    const valueMappings = this.form.get(featureFrom.id.toString()).get('valueMappings') as FormArray;
    valueMappings.clear();
  }

  modalClose() {
    this.windowRef.close();
  }

  saveTemplateMapping() {
    if (this.form.invalid) {
      return;
    }

    let mapping: FeaturesMapping = this.form.value;

    if (mapping) {
      const newMapping = {};

      for (const sourceFeatureId in mapping) {
        if (mapping[sourceFeatureId] && mapping[sourceFeatureId].destFeatureId) {
          newMapping[sourceFeatureId] = mapping[sourceFeatureId];
        }
      }
      mapping = newMapping;
    }

    this.formControl.setValue(mapping);
    this.updateTemplateMapping.next(true);
    this.modalClose();
  }
}
